import * as React from 'react';

import { flowRight as compose } from 'lodash';

import { TagModel, ProductTileModel } from '@notino/react-styleguide';

import { useTagAttributes } from '@components/Universals/ProductItem/useTagAttributes';
import { useNewPdDesignEnabled } from '@containers/ProductDetailContainer/ProductDetail/hooks/useNewPdDesignEnabled';
import {
  IStock,
  AttributeType,
  AttributeTypes,
  MaximumVolumeInPercent,
} from '@containers/ProductListing/model';

import { FormattedTooltip } from './FormattedTooltip';
import {
  labelAttributes,
  ILabelAttributes,
  ILabelAttribute,
  ClientOnlyAttributeTypes,
  ITagsProps,
} from './model';
import { StyledTag, StyledTagWrapper } from './styled';

interface LabelPair {
  label: Label;
  attributes: ILabelAttribute;
}

type Label = AttributeTypes | ClientOnlyAttributeTypes;

/**
 * Returns an object of variant attributes, where keys are attributes (label) and values are its properties.
 * It respects the order according to labelAttributes keys
 */
export const getLabelAttrs = (attributes: Label[]): Partial<ILabelAttributes> =>
  Object.keys(labelAttributes)
    .map((label) => attributes.find((variantLabel) => variantLabel === label))
    .filter((element) => element !== undefined)
    .reduce((acc, label) => {
      acc[label] = labelAttributes[label];
      return acc;
    }, {});

export const getPairs = (attrs: Partial<ILabelAttributes>): LabelPair[] =>
  Object.keys(attrs).reduce(
    (acc, label) => [...acc, { label, attributes: labelAttributes[label] }],
    []
  );

export const resolveDamageAttribute = (attributeValue: {
  volumeInPercent: number;
}): ClientOnlyAttributeTypes.Damaged | ClientOnlyAttributeTypes.Returned => {
  return attributeValue.volumeInPercent === MaximumVolumeInPercent
    ? ClientOnlyAttributeTypes.Damaged
    : ClientOnlyAttributeTypes.Returned;
};

export const mapClientOnlyAttributes = (
  label: AttributeTypes,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attributeValue: any
): Label => {
  if (label === AttributeTypes.Damage) {
    return resolveDamageAttribute(attributeValue);
  }

  return label;
};

export const transformAttributesToTags = (
  attributes: Record<string, unknown> = {}
): ProductTileModel.TagAttributes[] => {
  return Object.keys(attributes)
    .filter((key) => Boolean(attributes[key]))
    .map((key: AttributeTypes) => {
      const label = mapClientOnlyAttributes(key, attributes[key]);
      return ProductTileModel.TagAttributes[label];
    })
    .filter(Boolean);
};

const addClientOnlyAttributes = (attributes: AttributeType): Label[] =>
  Object.keys(attributes).reduce(
    (acc, label) => [
      ...acc,
      mapClientOnlyAttributes(label as AttributeTypes, attributes[label]),
    ],
    []
  );

export const getRenderableAttributes = compose(
  getLabelAttrs,
  addClientOnlyAttributes
) as (attributes?: AttributeType) => Partial<ILabelAttributes>;

export const Tags: React.FC<ITagsProps> = ({
  type = TagModel.Types.default,
  onClick,
  nowrapLg = false,
  ...props
}) => {
  const newDesign = useNewPdDesignEnabled();
  const tagAttributes = useTagAttributes(props.attributes, {
    multiple: props?.multiple,
  });
  if (props.stockAvailability === IStock.outOfStock || !tagAttributes) {
    return null;
  }

  const size = props.small ? TagModel.Sizes.small : TagModel.Sizes.default;

  return (
    <StyledTagWrapper nowrapLg={nowrapLg}>
      {tagAttributes.map(
        ({ style, icon, tooltipMessage, label, attribute }) => (
          <FormattedTooltip
            key={label}
            wrapperEnabled={
              !props.small &&
              type === TagModel.Types.default &&
              !!tooltipMessage
            }
            tooltipMessage={tooltipMessage}
          >
            <StyledTag
              newDesign={newDesign}
              onClick={() => onClick?.(attribute)}
              key={label}
              tagStyle={style}
              icon={icon}
              tagType={type}
              tagSize={size}
              className={`pd-variant-tag-${label}`}
            >
              {label}
            </StyledTag>
          </FormattedTooltip>
        )
      )}
    </StyledTagWrapper>
  );
};
